import axios from "axios";
import {
  LoginResponse,
  UserPublic,
  Organization,
  TokenPair,
  Invite,
} from "../types/auth";

const API_BASE = import.meta.env.REACT_APP_API_URL;

const authApi = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

// Token storage
const TOKEN_KEY = "db_access_token";
const REFRESH_KEY = "db_refresh_token";

// Where a super_admin's OWN tokens are parked while they're impersonating
// someone. Kept under a separate key (rather than, say, a token stack) so
// that a crash, a hard refresh, or a closed tab can't strand you inside a
// customer account with no way back — on load we simply look for this key.
const ORIGIN_KEY = "db_impersonation_origin";

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function storeTokens(tokens: TokenPair) {
  localStorage.setItem(TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_KEY, tokens.refreshToken);
}

export function clearTokens() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ORIGIN_KEY);
}

// ---- Impersonation ----

export function getOriginTokens(): TokenPair | null {
  const raw = localStorage.getItem(ORIGIN_KEY);
  if (!raw) {return null;}
  try {
    const parsed = JSON.parse(raw);
    return parsed?.accessToken && parsed?.refreshToken ? parsed : null;
  } catch {
    return null;
  }
}

export function storeOriginTokens(tokens: TokenPair) {
  localStorage.setItem(ORIGIN_KEY, JSON.stringify(tokens));
}

export function clearOriginTokens() {
  localStorage.removeItem(ORIGIN_KEY);
}

/**
 * Mint a session for another user. super_admin only; the backend refuses
 * self-impersonation, deactivated users, and other super_admins.
 */
export async function impersonateUser(
  userId: string,
  accessToken: string,
): Promise<LoginResponse> {
  const { data } = await authApi.post(
    `/api/admin/users/${userId}/impersonate`,
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

/**
 * Decode the impersonation stamp out of an access token, if present. The
 * backend re-stamps this on every refresh, so it stays true for the whole
 * life of the session rather than just the first 15 minutes.
 */
export function readImpersonation(
  accessToken: string | null,
): { impersonatorEmail: string } | null {
  if (!accessToken) {return null;}
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1]));
    if (!payload?.impersonatedBy) {return null;}
    return { impersonatorEmail: payload.impersonatorEmail || "a super admin" };
  } catch {
    return null;
  }
}

// Auth API calls

export async function login(
  email: string,
  password: string,
): Promise<LoginResponse> {
  const { data } = await authApi.post("/api/auth/login", { email, password });
  storeTokens(data.tokens);
  return data;
}

export async function requestMagicLink(
  email: string,
): Promise<{ message: string }> {
  const { data } = await authApi.post("/api/auth/magic-link", { email });
  return data;
}

export async function verifyMagicLink(token: string): Promise<LoginResponse> {
  const { data } = await authApi.post("/api/auth/magic-link/verify", { token });
  storeTokens(data.tokens);
  return data;
}

export async function refreshTokens(): Promise<TokenPair> {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {throw new Error("No refresh token");}

  const { data } = await authApi.post("/api/auth/refresh", { refreshToken });
  storeTokens(data.tokens);
  return data.tokens;
}

export async function logout(): Promise<void> {
  const refreshToken = getStoredRefreshToken();
  try {
    await authApi.post("/api/auth/logout", { refreshToken });
  } catch {
    // Ignore logout errors
  }
  clearTokens();
}

export async function getMe(
  accessToken: string,
): Promise<{ user: UserPublic; organization: Organization | null }> {
  const { data } = await authApi.get("/api/auth/me", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function getInviteInfo(
  token: string,
): Promise<{ email: string; role: string; organizationName: string }> {
  const { data } = await authApi.get(`/api/auth/invite/${token}`);
  return data;
}

export async function acceptInvite(
  token: string,
  password: string,
  firstName?: string,
  lastName?: string,
): Promise<LoginResponse> {
  const { data } = await authApi.post("/api/auth/invite/accept", {
    token,
    password,
    firstName,
    lastName,
  });
  storeTokens(data.tokens);
  return data;
}

export async function updateMyProfile(
  updates: { firstName?: string; lastName?: string; email?: string },
  accessToken: string,
): Promise<{ user: UserPublic; organization: Organization | null }> {
  const { data } = await authApi.patch("/api/auth/me", updates, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string,
  accessToken: string,
): Promise<TokenPair> {
  const { data } = await authApi.post(
    "/api/auth/change-password",
    { currentPassword, newPassword },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  storeTokens(data.tokens);
  return data.tokens;
}

// Admin API calls

export async function getUsers(accessToken: string): Promise<UserPublic[]> {
  const { data } = await authApi.get("/api/admin/users", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Identity Graph monitoring (super_admin only)

export interface IdentityGraphStats {
  generatedAt: string;
  period: "month" | "week";
  rangeStart: string;
  rangeEnd: string;
  graph: {
    totalProfiles: number;
    totalKeysApprox: number;
    keysAreEstimate: boolean;
    lastSnapshotDay: string | null;
    baselineProfiles: number;
    baselineAt: string | null;
    profilesAddedSinceBaseline: number;
  };
  summary: {
    runs: number;
    filesProcessed: number;
    rowsProcessed: number;
    newProfiles: number;
    duplicates: number;
    failed: number;
    newRatePct: number;
    dedupRatePct: number;
    failRatePct: number;
    netNewProfiles: number;
    avgNewPerActiveDay: number;
    daysWithActivity: number;
    prevPeriodNewProfiles: number;
    newProfilesChangePct: number;
  };
  daily: Array<{
    day: string;
    totalProfiles: number;
    totalKeysApprox: number;
    rowsProcessed: number;
    newProfiles: number;
    duplicates: number;
    failed: number;
    runs: number;
    files: number;
  }>;
}

export async function getIdentityGraphStats(
  accessToken: string,
  period: "month" | "week" = "month",
): Promise<IdentityGraphStats> {
  const { data } = await authApi.get(
    `/api/admin/identity-graph?period=${period}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

export async function triggerIdentityGraphSnapshot(
  accessToken: string,
): Promise<any> {
  const { data } = await authApi.post(
    "/api/admin/identity-graph/snapshot",
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

// ---- Enrichment dead-letter (async enrichment parking) ----
export interface EnrichDeadLetterItem {
  lead_id: string;
  data_body_id: string;
  vertebra_id: string | null;
  source: string | null;
  reason: string | null;
  attempts: number;
  first_failed_at: string;
  last_attempt_at: string;
}
export interface EnrichDeadLetterResponse {
  total: number;
  items: EnrichDeadLetterItem[];
}

export async function getEnrichmentDeadLetter(
  accessToken: string,
): Promise<EnrichDeadLetterResponse> {
  const { data } = await authApi.get("/api/admin/identity-graph/dead-letter", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function rerunEnrichmentDeadLetter(
  accessToken: string,
  leadId: string,
  dataBodyId: string,
): Promise<{ ok: boolean }> {
  const { data } = await authApi.post(
    "/api/admin/identity-graph/dead-letter/rerun",
    { leadId, dataBodyId },
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  return data;
}

// ---- Automations (RetargetIQ pull jobs board) ----
// The full JobSpec the editor reads/writes. Kept loose (index signature + `any` on the
// polymorphic mode/destination) so the frontend doesn't have to import backend types and so
// fields we don't yet surface in the UI survive a round-trip untouched. The backend's
// validateSpec is the real gate.
export interface JobSpecLite {
  name: string;
  enabled: boolean;
  mode: { kind: 'intent' | 'filters' | 'grid' | 'segsweep' | 'datamoon_feed'; [k: string]: any };
  freshnessDays: number;
  dailyBudget: number;
  allocation?: string;
  destination: { type: string; dataBodyId?: string; apiBaseUrl?: string; [k: string]: any };
  fields?: string[];
  dedupeInFlightBy?: string;
  emailLastSeenDays?: number;
  staticFields?: Record<string, string>;
  seedGraph?: boolean;
  schedule?: string;
  frequency?: 'daily' | 'weekly';
  runAt?: string;            // 'HH:MM' PT — feed (datamoon_feed) jobs run via the hourly @due cron
  [k: string]: any;
}
export interface AutomationRow {
  jobName: string;
  client: string;
  enabled: boolean;
  mode: string;
  topic: string | null;
  topicIds: string[] | null;
  score: string[] | null;
  lookbackDays: number | null;
  requireEmail: boolean | null;
  requireMobile: boolean | null;
  emailLastSeenDays: number | null;
  states: string | null;
  freshnessDays: number;
  dailyCap: number | null;
  dedupeBy: string | null;
  schedule: string;
  destinationType: string;
  dataBodyId: string | null;
  dataBodyName: string | null;
  dataBodyStatus: string | null;
  leadsInBody: number | null;
  leadsToday: number | null;
  lastLeadReceived: string | null;
  flags: string[];
  health: "ok" | "warn" | "error";
  spec: JobSpecLite;   // full editable spec; the display fields above are a projection of it
}
export interface AutomationsResponse {
  generatedAt: string;
  count: number;
  statsPending?: boolean; // lead-count cache was cold; counts still computing in background
  automations: AutomationRow[];
}
export async function getAutomations(
  accessToken: string,
): Promise<AutomationsResponse> {
  const { data } = await authApi.get("/api/admin/automations", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Update an existing job's full spec. `name` in the URL must equal spec.name (rename disallowed
// here). Backend runs validateSpec first (400 on a bad shape). Returns { updated, name }.
export async function updateAutomation(accessToken: string, name: string, spec: JobSpecLite): Promise<{ updated: boolean; name: string }> {
  const { data } = await authApi.patch(`/api/admin/automations/${encodeURIComponent(name)}`, { spec }, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Create a new job. 409 if the name already exists. Returns { id, name }.
export async function createAutomation(accessToken: string, spec: JobSpecLite): Promise<{ id: string; name: string }> {
  const { data } = await authApi.post('/api/admin/automations', { spec }, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Toggle a job on/off (the safety gate) without rewriting the whole spec. Returns { name, enabled }.
export async function setAutomationEnabled(accessToken: string, name: string, enabled: boolean): Promise<{ name: string; enabled: boolean }> {
  const { data } = await authApi.post(`/api/admin/automations/${encodeURIComponent(name)}/enabled`, { enabled }, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Topic typeahead — resolve RetargetIQ intent-topic labels → ids for the automation editor.
export interface TopicHit { id: string; label: string; }
export async function searchAutomationTopics(accessToken: string, q: string): Promise<TopicHit[]> {
  const { data } = await authApi.get('/api/admin/automations/topics/search', {
    params: { q },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data.hits ?? [];
}

// ---- Destinations registry (reusable delivery targets; migration 033) ----
// Stores env-var NAMES, never secrets. Jobs pick one to fill spec.destination.
export type DestinationKind = 'data_body' | 'custom_api';
// Custom API settings — same key names as the Custom API leg-node config.
export interface CustomApiConfig {
  webhookUrl: string;
  webhookMethod: 'POST' | 'PUT' | 'GET';
  bodyFormat?: 'json' | 'form';
  webhookHeaders?: Record<string, string>;
  fieldMapping: Record<string, string>;   // destination field -> lead field
  requiredFields?: string[];
  staticValues?: Record<string, string>;
  sendRateMax?: number;
  sendRateWindowSeconds?: number;
  successCondition?: { field: string; operator: 'equals' | 'not_equals' | 'contains' | 'exists' | 'regex'; value: string };
}
export interface AutomationDestination {
  id: string;
  kind: DestinationKind;              // data_body = CSV import into a body; custom_api = pull pushes each lead to an API
  name: string;
  apiBaseUrl: string | null;
  dataBodyId: string | null;          // null for custom_api
  apiConfig: CustomApiConfig | null;  // custom_api only
  loginEmailEnv: string | null;
  loginPasswordEnv: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface DestinationInput {
  kind?: 'data_body';
  name: string;
  apiBaseUrl?: string | null;
  dataBodyId: string;
  loginEmailEnv?: string | null;
  loginPasswordEnv?: string | null;
  notes?: string | null;
}
export interface CustomApiDestinationInput {
  kind: 'custom_api';
  name: string;
  notes?: string | null;
  apiConfig: CustomApiConfig;
}
export type AnyDestinationInput = DestinationInput | CustomApiDestinationInput;
const authHeader = (accessToken: string) => ({ headers: { Authorization: `Bearer ${accessToken}` } });

export async function listAutomationDestinations(accessToken: string): Promise<AutomationDestination[]> {
  const { data } = await authApi.get('/api/admin/automations/destinations', authHeader(accessToken));
  return data.destinations ?? [];
}
export async function createAutomationDestination(accessToken: string, destination: AnyDestinationInput): Promise<AutomationDestination> {
  const { data } = await authApi.post('/api/admin/automations/destinations', { destination }, authHeader(accessToken));
  return data.destination;
}
export async function updateAutomationDestination(accessToken: string, id: string, destination: AnyDestinationInput): Promise<AutomationDestination> {
  const { data } = await authApi.patch(`/api/admin/automations/destinations/${encodeURIComponent(id)}`, { destination }, authHeader(accessToken));
  return data.destination;
}
export async function deleteAutomationDestination(accessToken: string, id: string): Promise<void> {
  await authApi.delete(`/api/admin/automations/destinations/${encodeURIComponent(id)}`, authHeader(accessToken));
}

// ---- Test runs (on-demand single-job --limit-rows=1 run) ----
export interface AutomationRun {
  id: string;
  jobName: string;
  mode: 'test';
  status: 'running' | 'done' | 'error';
  exitCode: number | null;
  output: string[];
  startedAt: string;
  finishedAt: string | null;
}
export async function startAutomationTestRun(accessToken: string, name: string): Promise<string> {
  const { data } = await authApi.post(`/api/admin/automations/${encodeURIComponent(name)}/run/test`, {}, authHeader(accessToken));
  return data.runId;
}
export async function getAutomationRun(accessToken: string, runId: string): Promise<AutomationRun> {
  const { data } = await authApi.get(`/api/admin/automations/runs/${encodeURIComponent(runId)}`, authHeader(accessToken));
  return data.run;
}

export interface IdentityGraphAttributeFill {
  computedAt: string | null;
  sampleN: number;
  categories: Array<{
    group: string;
    label: string;
    attributes: Array<{
      key: string;
      label: string;
      pct: number;
      presentN: number;
    }>;
  }>;
}

export async function getIdentityGraphAttributeFill(
  accessToken: string,
): Promise<IdentityGraphAttributeFill> {
  const { data } = await authApi.get(
    "/api/admin/identity-graph/attribute-fill",
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

export interface FieldMatrixRow {
  source: string;
  sourceLabel: string;
  group: string;
  attribute: string;
  label: string;
  type: string;
  theirField: string;
  normalizer: string;
  accepts: string;
  drops: string;
  output: string;
  fillPct: number;
  presentN: number;
  sampleN: number;
  pctDelta: number | null;
  rawExamples: string[];
  valueDist: Array<{ v: string; n: number }>;
  flags: string[];
}

export interface FieldMatrix {
  snapshotDate: string | null;
  prevSnapshotDate: string | null;
  computedAt: string | null;
  sources: Array<{ source: string; label: string }>;
  rows: FieldMatrixRow[];
}

export async function getIdentityGraphFieldMatrix(
  accessToken: string,
): Promise<FieldMatrix> {
  const { data } = await authApi.get("/api/admin/identity-graph/field-matrix", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function createUser(
  userData: any,
  accessToken: string,
): Promise<UserPublic> {
  const { data } = await authApi.post("/api/admin/users", userData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function updateUser(
  userId: string,
  updates: any,
  accessToken: string,
): Promise<UserPublic> {
  const { data } = await authApi.patch(`/api/admin/users/${userId}`, updates, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function deactivateUser(
  userId: string,
  accessToken: string,
): Promise<UserPublic> {
  const { data } = await authApi.post(
    `/api/admin/users/${userId}/deactivate`,
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

export async function activateUser(
  userId: string,
  accessToken: string,
): Promise<UserPublic> {
  const { data } = await authApi.post(
    `/api/admin/users/${userId}/activate`,
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

// Reset a user's password. Pass a string to set a specific value, or
// omit `password` to have the server generate a 14-char alphanumeric.
// The plaintext is returned so the admin can hand it to the user.
export async function resetUserPassword(
  userId: string,
  accessToken: string,
  password?: string,
): Promise<{ password: string }> {
  const { data } = await authApi.post(
    `/api/admin/users/${userId}/reset-password`,
    password ? { password } : {},
    { headers: { Authorization: `Bearer ${accessToken}` } },
  );
  return data;
}

export async function getOrganizations(
  accessToken: string,
): Promise<Organization[]> {
  const { data } = await authApi.get("/api/admin/organizations", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

// Caller's own organization, including timezone and the list of
// supported timezone values the admin UI can offer. Use this for the
// Org Settings page; the super-admin getOrganizations() above returns
// ALL orgs.
export async function getOwnOrganization(
  accessToken: string,
): Promise<Organization & { supportedTimezones: string[] }> {
  const { data } = await authApi.get("/api/admin/organization", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function updateOwnOrganization(
  updates: { name?: string; timezone?: string },
  accessToken: string,
): Promise<Organization> {
  const { data } = await authApi.patch("/api/admin/organization", updates, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function createOrganization(
  name: string,
  accessToken: string,
): Promise<Organization> {
  const { data } = await authApi.post(
    "/api/admin/organizations",
    { name },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

export async function getInvites(accessToken: string): Promise<Invite[]> {
  const { data } = await authApi.get("/api/admin/invites", {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  return data;
}

export async function sendInvite(
  email: string,
  role: string,
  accessToken: string,
  orgId?: string,
): Promise<Invite> {
  const { data } = await authApi.post(
    "/api/admin/invites",
    { email, role, orgId },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return data;
}

export async function revokeInvite(
  inviteId: string,
  accessToken: string,
): Promise<void> {
  await authApi.delete(`/api/admin/invites/${inviteId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}
